/* -----------------Helpers & Hooks--------------- */
import { getIsOlxExam, getMappedExams } from "@helpers/exams";
import { titleCase } from "@memorang/helpers/src/common";

/* -----------------Types--------------- */
import type {
	ExamSubType,
	LegacyReport,
	MixedItemDist,
	PracticeTestExam,
} from "@memorang/types/src/tag";
import type { ChipProps } from "react-native-paper";
import type { EXAM_STATUS } from "types/tag";

import { formatExamDate } from "@helpers/date";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
import pluralize from "pluralize";

export type PracticeTestsViewSection = {
	title: string;
	data: Array<{
		list: {
			collectionId: number;
			title: string;
			description: string;
			status: EXAM_STATUS;
			numericId: number;
			cta: string;
			id: string;
			score: number;
			hasExamAccess?: boolean;
			beginStartDate?: string;
			endStartDate?: string;
			subType: ExamSubType;
			legacyReport?: LegacyReport;
			migratedSessionReport?: LegacyReport;
		}[][];
	}>;
	rightChip?: {
		text: string;
		icon: string;
		tooltip: string;
		mode: ChipProps["mode"];
	};
	type: "practice-tests" | "quiz";
};

const getStats = (
	numQuiz: number,
	numPracticeTests: number,
	examDate?: string,
	diagnosticLabel?: Record<string, string>,
) => {
	const practiceTestLabel = useAppStore.getState().exam.practiceTest?.label;
	const stats = [
		{
			icon: "clipboard-check-multiple-outline" as const,
			label: pluralize(
				practiceTestLabel?.toLocaleLowerCase() ?? "practice tests",
				numPracticeTests,
			),
			value: numPracticeTests,
		},
		...(numQuiz
			? [
					{
						label: `${
							diagnosticLabel
								? diagnosticLabel[i18n.language]
								: "diagnostic assessment"
						}`,
						value: numQuiz,
						icon: "format-list-checks" as const,
					},
				]
			: []),
		// {
		// 	icon: "text-box-multiple-outline" as const,
		// 	label: "items",
		// 	value: numTotalItems,
		// },
		...(examDate
			? [
					{
						label: formatExamDate(examDate),
						icon: "calendar-outline" as const,
						value: "Exam Date:",
					},
				]
			: []),
	];
	return stats;
};

export const getPracticeTestsQuizzesAndExams = (exams: PracticeTestExam[]) => {
	const practiceTests = exams
		.filter((exam) => {
			return getIsOlxExam(exam.id)
				? exam.hasAccess
				: exam.subType === "PRACTICE_TEST";
		})
		.sort((a, b) => {
			const aIsOlx = getIsOlxExam(a.id);
			const bIsOlx = getIsOlxExam(b.id);
			if (aIsOlx && !bIsOlx) {
				return -1;
			}
			if (!aIsOlx && bIsOlx) {
				return 1;
			}
			return 0;
		});
	const practiceTestQuizzes = exams
		.filter((exam) => exam.subType === "DIAGNOSTIC" && exam.numItems)
		.slice(0, 2);
	return {
		practiceTests,
		practiceTestQuizzes,
	};
};

export const getExamStatusIcon = (status: EXAM_STATUS) => {
	switch (status) {
		case "COMPLETE":
			return "check";
		case "ACTIVE":
			return "timer-sand";
		case "LEGACYREPORT":
		case "MIGRATEDREPORT":
			return "file-chart-outline";
		default:
			return "";
	}
};
export const getPracticeTestsData = (
	response: MixedItemDist,
	examName: string,
	examDate?: string,
	diagnosticLabel?: Record<string, string>,
) => {
	const { exams = [], hasAccess } = response;
	const { practiceTests, practiceTestQuizzes } =
		getPracticeTestsQuizzesAndExams(exams);
	const practiceTestsLabel = useAppStore.getState().exam.practiceTest?.label;

	const mappedExams = getMappedExams(practiceTests);
	const stats = getStats(
		practiceTestQuizzes?.length,
		mappedExams?.length,
		examDate,
		diagnosticLabel,
	);
	const mappedQuizzes = getMappedExams(practiceTestQuizzes);

	const sections: PracticeTestsViewSection[] = [
		{
			title: practiceTestsLabel || "Practice Tests",
			data: [
				{
					list: [mappedExams],
				},
			],
			...(hasAccess
				? {}
				: {
						rightChip: {
							text: "Premium",
							icon: "crown-outline",
							mode: "flat" as const,
							tooltip: `Full-length, premium practice tests for the ${examName} exam`,
						},
					}),
			type: "practice-tests" as const,
		},
	];

	const freeQuizzes = mappedQuizzes.length
		? {
				title: diagnosticLabel
					? titleCase(diagnosticLabel[i18n.language])
					: "Diagnostic Assessments",
				data: [
					{
						type: "quiz",
						list: [mappedQuizzes],
					},
				],
				rightChip: {
					text: "Free",
					icon: "gift-outline",
					mode: "outlined" as const,
					tooltip: `${
						diagnosticLabel
							? titleCase(diagnosticLabel[i18n.language])
							: "Free diagnostic assessments"
					} for the ${examName} exam`,
				},
				type: "quiz" as const,
			}
		: null;

	const sectionsWithRecommendedItems = freeQuizzes
		? [freeQuizzes, ...sections]
		: sections;
	return {
		stats,
		hasAccess,
		sections: sectionsWithRecommendedItems,
		numExams: exams.length,
		mappedQuizzes,
	};
};
